import { useMemo } from 'react';
import { Box } from '@mui/material';

import { SearchForm, TableContainer, TableHeader, Button } from '@occc-app/design-system';
import { RetailerNetworkLocationsWithPerformanceExcerptFilter } from '@occc/graphql';
import { useSearchParams } from '@occc-app/hooks/useSearchParams';
import { usePaginationParams } from '@occc-app/hooks/usePaginationParams';
import { Can } from '@occc-app/App/PolicyBasedAccessControl';
import { AddLocationsToRetailerNetworkLocationGroup } from '@occc-app/App/PolicyBasedAccessControl/userActions';

import RetailerNetworkLocationGroupLocationsTable from './retailer-network-location-group-locations-table.component';
import { useLocationGroupSorting } from './use-location-group-sorting.hook';

import { useGetRetailerNetworkLocationsWithPerformanceExcerpt } from '../../data-access/queries';
import {
  LOCATIONS_AFTER_PARAM,
  LOCATIONS_BEFORE_PARAM,
  LOCATIONS_PARAM_NAMES,
} from '../retailer-network-locations-configuration/retailer-network-locations-configuration.constants';

type RetailerNetworkLocationGroupLocationsTableContainerProps = {
  retailerNetworkUuid: string;
  filter: Omit<RetailerNetworkLocationsWithPerformanceExcerptFilter, 'search'>;
  setAddLocationsDialogOpen: (open: boolean) => void;
  setRemoveLocationDialogOpen: (locationUuid: string | null) => void;
  handleOpenLocationDetailsPage: (locationUuid: string) => void;
};

export const RetailerNetworkLocationGroupLocationsTableContainer = ({
  retailerNetworkUuid,
  filter,
  setRemoveLocationDialogOpen,
  setAddLocationsDialogOpen,
  handleOpenLocationDetailsPage,
}: RetailerNetworkLocationGroupLocationsTableContainerProps) => {
  const [locationsSearch, setLocationsSearch] = useSearchParams(LOCATIONS_PARAM_NAMES);
  const [locationsPaginationInfo, onLocationsPageChange] = usePaginationParams({
    beforeParamName: LOCATIONS_BEFORE_PARAM,
    afterParamName: LOCATIONS_AFTER_PARAM,
  });
  const [sort, toggleSorting] = useLocationGroupSorting();

  const locationsData = useGetRetailerNetworkLocationsWithPerformanceExcerpt({
    sort,
    filter: {
      ...filter,
      search: locationsSearch,
    },
    paginationInfo: locationsPaginationInfo,
    onPageChange: onLocationsPageChange,
  });

  const tableActions = useMemo(() => {
    const onSearchSubmit = ({ search = '' }: { search?: string }) => {
      setLocationsSearch(search);
    };

    return (
      <Box display='flex'>
        <SearchForm onSubmit={onSearchSubmit} initialValues={{ search: locationsSearch }} />
        <Can I={AddLocationsToRetailerNetworkLocationGroup({ uuid: retailerNetworkUuid })}>
          <Button
            variant='contained'
            sx={{ minWidth: 'fit-content', marginLeft: 2 }}
            onClick={() => setAddLocationsDialogOpen(true)}
            role='button'
          >
            Add Locations
          </Button>
        </Can>
      </Box>
    );
  }, [locationsSearch, setLocationsSearch, setAddLocationsDialogOpen, retailerNetworkUuid]);

  return (
    <TableContainer sx={{ mb: 1 }}>
      <TableHeader title='All Locations' actions={tableActions} />
      <RetailerNetworkLocationGroupLocationsTable
        retailerNetworkUuid={retailerNetworkUuid}
        locationsData={locationsData}
        sortingParameters={sort}
        toggleSorting={toggleSorting}
        setRemoveLocationDialogOpen={setRemoveLocationDialogOpen}
        handleOpenLocationDetailsPage={handleOpenLocationDetailsPage}
      />
    </TableContainer>
  );
};
